// Create purchase orders — on the project's Shipping tab, under the Shipping
// plan (Stephanie 2026-10-13: "this option should only pop up once the
// shipping information has been entered and the rep selects create PO").
//
// Shows nothing to act on until every item still to go on a PO has its
// shipping filled in; then the same Create-POs step the quote page used to
// carry: POST /orders/:id/pos with the same pre-checks (shipping address,
// supplier, artwork — derivePoPreconditions), the same $0-cost confirm
// (zeroCostProductLineNames) and missing-artwork "create anyway" override, and
// the same created / flagged-for-review result list. After the POs exist the
// step reads "Review & send PO" / "Add tracking", like the quote.
//
// The quote page keeps its own Create-POs mutation for the cancelled-leg
// re-create (E3); this mirrors it rather than sharing it — kept in step by
// qa-line-item-shipping.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { derivePoPreconditions, lineNeedsArtwork, zeroCostProductLineNames } from "@merchd/shared";
import { ApiError, api } from "@/lib/api";
import { Package } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { NextActionCta } from "@/components/orders/NextActionCta";
import { shortShipTo, type ShipToInput } from "@/components/orders/shortShipTo";
import { fetchVariantOptions, variantOptionsQueryKey } from "@/components/orders/SizeBreakdownEditor";

export interface CreatePoOrder {
  id: string;
  projectId: string;
  shippingAddressId: string | null;
  shippingAddressSnapshot: ShipToInput | null;
  /** Quote "Blank order" box — null/absent until the DB column exists. */
  blankOrder?: boolean | null;
}

export interface CreatePoLine {
  id: string;
  serviceType: string;
  description: string | null;
  netCost: string | null;
  decorationPath?: string | null;
  productId?: string | null;
  /** The line's color/size breakdown rows (empty = none yet). */
  variants?: unknown[];
  productSnapshot: { name?: string; vendorId?: string; imageUrls?: unknown } | null;
  decorations?: Array<{
    /** 'item_costs' = the "+ Add cost" holder, not an imprint location. */
    kind?: string;
    location: string | null;
    logoFileId?: string | null;
    logoName?: string | null;
    proofType?: string | null;
  }>;
}

/** One product row of the Shipping plan (OrderShippingTab's LineShippingRow). */
export interface PlanRow {
  lineItemId: string;
  hasStagedRow: boolean;
  coveredByPoNumber: number | null;
  coveredByPoId: string | null;
  supplierVendorId: string | null;
  supplierVendorName: string | null;
  productSnapshot: { name?: string } | null;
  description: string | null;
}

export function CreatePurchaseOrders({
  order,
  lines,
  canCreatePo,
  plan,
  selection,
}: {
  order: CreatePoOrder;
  lines: CreatePoLine[];
  canCreatePo: boolean;
  plan: PlanRow[];
  /** The items ticked in the Shipping plan (the same ticks Edit Selected uses). */
  selection: { ids: string[]; clear: () => void };
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [artworkOverride, setArtworkOverride] = useState(false);
  const [zeroCostNames, setZeroCostNames] = useState<string[] | null>(null);
  const [createdPos, setCreatedPos] = useState<Array<{ id: string; poNumber: number; poType: string }>>([]);
  const [orphanedPos, setOrphanedPos] = useState<Array<{ id: string; poNumber: number }>>([]);

  const quoteHref = `/projects/${order.projectId}/quotes/${order.id}`;
  const productLines = lines.filter((l) => l.serviceType === "product");
  const variantOptions = useQuery({
    queryKey: [...variantOptionsQueryKey(order.id), lines.map((l) => `${l.id}:${l.productId ?? ""}`).join(",")],
    queryFn: () => fetchVariantOptions(order.id),
    staleTime: 5 * 60_000,
  });
  const itemName = (l: CreatePoLine) => l.productSnapshot?.name ?? l.description ?? "Item";

  const mutation = useMutation({
    mutationFn: async (opts: { lineIds?: string[]; skipArtwork?: boolean; skipZeroCost?: boolean } = {}) =>
      api<{
        pos: Array<{ po: { id: string; poNumber: number; poType: string }; line_count: number }>;
        orphaned: Array<{ id: string; poNumber: number }>;
      }>(`/orders/${order.id}/pos`, {
        method: "POST",
        body: JSON.stringify({
          ...(opts.skipArtwork ? { skip_artwork_check: true } : {}),
          ...(opts.skipZeroCost ? { skip_zero_cost_check: true } : {}),
          // One supplier at a time (Stephanie 2026-10-13: "sometimes we may be
          // waiting on info from one vendor but want to get the rest in").
          // Just the ticked items — how a second PO to the same supplier is made
          // (Stephanie 2026-10-13). The server never touches existing POs here.
          ...(opts.lineIds ? { only_line_ids: opts.lineIds } : {}),
        }),
      }),
    onError: (err) => {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; reason?: string } | null;
        if (body?.reason === "missing_artwork") {
          // Not a blank order → the logo is required, no "anyway" (Stephanie
          // 2026-10-14).
          const required = order.blankOrder === false;
          setArtworkOverride(!required);
          setError(
            required
              ? `Logo needed — ${body.error ?? "add the logo on the quote"}. Add it on the quote, or tick Blank order if there's no logo.`
              : body.error
                ? `Artwork specs needed — ${body.error}. Add them on the quote first, or create the PO anyway.`
                : "Some items don't have artwork specs yet. Add artwork on the quote first, or create the PO anyway.",
          );
          return;
        }
        if (body?.reason === "no_shipping_address") {
          setError("Add a shipping address to the quote first, then create the PO.");
          return;
        }
        if (body?.reason === "zero_cost") {
          setZeroCostNames([]);
          return;
        }
        setError(body?.error ?? `Create PO failed (${err.status}).`);
      } else {
        setError("Create PO failed. Try again or check your connection.");
      }
    },
    onSuccess: (res, vars) => {
      setError(null);
      setArtworkOverride(false);
      setZeroCostNames(null);
      void queryClient.invalidateQueries({ queryKey: ["project", order.projectId, "purchase-orders"] });
      void queryClient.invalidateQueries({ queryKey: ["orders", order.id, "line-shipping"] });
      // An approved quote may have just been switched to an order — refresh the project.
      void queryClient.invalidateQueries({ queryKey: ["project", order.projectId] });
      // Every item now on a PO (the last supplier, or "Create all") → go to
      // the POs tab, new POs marked, each with "Email to supplier" (Stephanie
      // 2026-10-15, PO idea 2). Otherwise stay here: the new PO shows in the
      // supplier list so the rep can carry on with the other suppliers.
      const left = groups.flatMap((g) => g.rows).filter((r) => !(vars?.lineIds ?? []).includes(r.lineItemId));
      if (left.length === 0 && (res.orphaned ?? []).length === 0 && res.pos.length > 0) {
        navigate(`/projects/${order.projectId}/pos?new=${res.pos.map((p) => p.po.id).join(",")}`);
        return;
      }
      setCreatedPos(res.pos.map((p) => p.po));
      selection.clear();
      setOrphanedPos(res.orphaned ?? []);
    },
  });

  // The PO groups exactly as the server makes them: one supplier PO per
  // product vendor (productSnapshot.vendorId), lines already on a live PO left
  // out. Each group can be created on its own once ITS items are filled in.
  const vendorOf = (lineId: string) => productLines.find((l) => l.id === lineId)?.productSnapshot?.vendorId ?? null;
  type Group = { vendorId: string; name: string; rows: PlanRow[]; lines: CreatePoLine[] };
  const groups: Group[] = [];
  for (const r of plan) {
    const vendorId = vendorOf(r.lineItemId);
    if (!vendorId || r.coveredByPoNumber != null) continue;
    let g = groups.find((x) => x.vendorId === vendorId);
    if (!g) {
      g = { vendorId, name: "", rows: [], lines: [] };
      groups.push(g);
    }
    g.rows.push(r);
    const line = productLines.find((l) => l.id === r.lineItemId);
    if (line) g.lines.push(line);
    if (!g.name && r.supplierVendorId === vendorId && r.supplierVendorName) g.name = r.supplierVendorName;
  }
  for (const g of groups) if (!g.name) g.name = g.rows.map((r) => r.productSnapshot?.name ?? r.description ?? "Item").join(", ");
  const covered = [...new Map(plan.filter((r) => r.coveredByPoId).map((r) => [r.coveredByPoId!, r])).values()];

  const [pendingVendor, setPendingVendor] = useState<string | null>(null);
  const [lastVendor, setLastVendor] = useState<string | undefined>(undefined);
  const [lastLineIds, setLastLineIds] = useState<string[] | undefined>(undefined);

  // Same $0-cost gate as the quote, on the lines this click would put on a PO.
  const start = (group: Group | null, skipArtwork: boolean, picked?: string[]) => {
    const fromSelection = picked !== undefined;
    setError(null);
    setArtworkOverride(skipArtwork);
    // Every create here names its items (only_line_ids): a supplier can have
    // more than one PO now, and the item-scoped path is the one that never
    // reuses or orphans another PO's decoration leg. (only_vendor_id stays for
    // the quote's cancelled-leg re-create, which DOES re-pair.)
    const lineIds = picked ?? (group ? group.rows : groups.flatMap((g) => g.rows)).map((r) => r.lineItemId);
    const vendorId = group?.vendorId;
    setLastVendor(vendorId);
    setLastLineIds(lineIds);
    const scope = productLines.filter((l) => lineIds.includes(l.id));
    const zero = zeroCostProductLineNames(scope.map((l) => ({ name: itemName(l), unitCost: l.netCost, isProduct: true })));
    if (zero.length > 0) {
      setZeroCostNames(zero);
      return;
    }
    setPendingVendor(fromSelection ? "selected" : (vendorId ?? "all"));
    mutation.mutate({ lineIds, skipArtwork }, { onSettled: () => setPendingVendor(null) });
  };

  const preconditionFor = (lines: CreatePoLine[]) =>
    derivePoPreconditions({
      hasShippingAddress: order.shippingAddressId !== null,
      blankOrder: order.blankOrder,
      productLines: lines.map((l) => ({
        id: l.id,
        itemName: itemName(l),
        hasVendor: typeof l.productSnapshot?.vendorId === "string",
        needsArtwork: lineNeedsArtwork(l.decorationPath, order.blankOrder),
        // Colors/sizes are required for a PO (Stephanie 2026-10-14).
        missingSizes:
          (l.variants ?? []).length === 0 &&
          (variantOptions.data ?? []).some((o) => o.lineId === l.id && o.needsBreakdown),
        decorations: (l.decorations ?? [])
          // The "+ Add cost" holder is not an imprint location — no artwork.
          .filter((d) => d.kind !== "item_costs")
          .map((d) => ({
          location: d.location,
          hasLogoFile: d.logoFileId != null,
          hasLogoName: !!d.logoName?.trim(),
          hasProofType: d.proofType != null,
        })),
      })),
    });

  // Everything already on a PO → the next step (send / tracking), as on the quote.
  if (groups.length === 0) {
    return (
      <NextActionCta
        orderId={order.id}
        projectId={order.projectId}
        hasProductLines={productLines.length > 0}
        canCreatePo={canCreatePo}
        onCreatePo={() => start(null, false)}
        onCreateAnyway={() => start(null, true)}
        precondition={preconditionFor([])}
        createPoPending={mutation.isPending}
        shipsTo={shortShipTo(order.shippingAddressSnapshot)}
        anchorBase={quoteHref}
      />
    );
  }

  const readyGroups = groups.filter((g) => g.rows.every((r) => r.hasStagedRow));
  const shipsTo = shortShipTo(order.shippingAddressSnapshot);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-brown-500">Purchase orders</h3>
        {shipsTo && (
          <p className="text-xs text-brown-400">
            <span className="font-semibold text-brown-500">Ships to:</span> {shipsTo}
          </p>
        )}
      </div>
      {canCreatePo && selection.ids.length > 0 && (() => {
        // Items already on a PO can be selected to edit shipping (the tab never
        // locks, Stephanie 2026-10-27) but never go on a second PO.
        const picked = plan.filter((r) => selection.ids.includes(r.lineItemId) && r.coveredByPoNumber == null);
        if (picked.length === 0) return null;
        const pickIds = picked.map((r) => r.lineItemId);
        const unstaged = picked.filter((r) => !r.hasStagedRow).length;
        const pre = preconditionFor(productLines.filter((l) => pickIds.includes(l.id)));
        const suppliers = new Set(picked.map((r) => vendorOf(r.lineItemId))).size;
        return (
          <div className="mb-3 rounded-lg border border-ink/20 bg-cream-50 px-4 py-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-brown-500">
                <span className="font-medium">{picked.length} {picked.length === 1 ? "item" : "items"} selected</span>
                {suppliers > 1 && <span className="ml-2 text-brown-400">· one PO per supplier</span>}
              </span>
              {unstaged > 0 ? (
                <span className="text-brown-400">Fill in shipping for the selected items first</span>
              ) : (
                <span className="flex flex-wrap items-center gap-3">
                  {!pre.ok && pre.overridable && (
                    <button
                      type="button"
                      onClick={() => start(null, true, pickIds)}
                      disabled={mutation.isPending}
                      className="text-sm font-semibold text-ink underline-offset-2 hover:underline disabled:opacity-50"
                    >
                      Create anyway
                    </button>
                  )}
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => start(null, false, pickIds)}
                    disabled={mutation.isPending || !pre.ok}
                  >
                    <Package className="h-4 w-4" aria-hidden />
                    {pendingVendor === "selected" ? "Creating…" : "Create PO from selected"}
                  </Button>
                </span>
              )}
            </div>
            {unstaged === 0 && !pre.ok && <p className="mt-2 text-brown-400">{pre.message}</p>}
            <p className="mt-1 text-xs text-brown-400">
              Only these items go on the PO. The rest can go on another PO to the same supplier later.
            </p>
          </div>
        );
      })()}
      <ul className="divide-y divide-brown-50 rounded-lg border border-brown-100">
        {covered.map((r) => (
          <li key={r.coveredByPoId} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm">
            <span className="text-brown-500">{r.supplierVendorName ?? "Supplier"}</span>
            <Link to={`/projects/${order.projectId}/pos/${r.coveredByPoId}`} className="font-semibold text-ink hover:text-muted">
              PO #{r.coveredByPoNumber} created →
            </Link>
          </li>
        ))}
        {groups.map((g) => {
          const unstaged = g.rows.filter((r) => !r.hasStagedRow).length;
          const pre = preconditionFor(g.lines);
          const busy = pendingVendor === g.vendorId;
          return (
            <li key={g.vendorId} className="px-4 py-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span>
                  <span className="font-medium text-brown-500">{g.name}</span>
                  <span className="ml-2 text-brown-400">
                    {g.rows.length} {g.rows.length === 1 ? "item" : "items"}
                  </span>
                </span>
                {unstaged > 0 ? (
                  <span className="text-brown-400">
                    Fill in shipping for {unstaged} {unstaged === 1 ? "item" : "items"}
                  </span>
                ) : !canCreatePo ? (
                  <span className="text-brown-400">Ready — a production rep or admin creates the PO</span>
                ) : (
                  <span className="flex flex-wrap items-center gap-3">
                    {!pre.ok && pre.overridable && (
                      <button
                        type="button"
                        onClick={() => start(g, true)}
                        disabled={mutation.isPending}
                        className="text-sm font-semibold text-ink underline-offset-2 hover:underline disabled:opacity-50"
                      >
                        Create anyway
                      </button>
                    )}
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => start(g, false)}
                      disabled={mutation.isPending || !pre.ok}
                    >
                      <Package className="h-4 w-4" aria-hidden />
                      {busy ? "Creating…" : "Create PO"}
                    </Button>
                  </span>
                )}
              </div>
              {unstaged === 0 && canCreatePo && !pre.ok && (
                <p className="mt-2 text-brown-400">
                  {pre.message}{" "}
                  {pre.reason === "no_shipping_address" && (
                    <a href={`${quoteHref}#order-addresses`} className="font-semibold text-ink hover:text-muted">
                      Set it on the quote →
                    </a>
                  )}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      {canCreatePo && groups.length > 1 && readyGroups.length === groups.length && (
        <div className="mt-3 flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={() => start(null, false)} disabled={mutation.isPending}>
            {pendingVendor === "all" ? "Creating…" : `Create all ${groups.length} POs`}
          </Button>
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-lg border border-rust-200 bg-rust-50 px-4 py-3 text-sm text-rust-600">
          <p>{error}</p>
          {artworkOverride && (
            <button
              type="button"
              onClick={() => start(groups.find((g) => g.vendorId === lastVendor) ?? null, true, lastLineIds)}
              disabled={mutation.isPending}
              className="mt-2 rounded-lg bg-ink px-3 py-1.5 text-xs font-semibold text-white hover:bg-ink-700 disabled:opacity-50"
            >
              {mutation.isPending ? "Creating…" : "Create PO anyway"}
            </button>
          )}
        </div>
      )}
      {zeroCostNames && (
        <div className="mb-4 rounded-lg border border-peach-300 bg-peach-100 px-4 py-3 text-sm text-brown-500">
          <p className="font-semibold">$0-cost items on this purchase order</p>
          <p className="mt-1 text-brown-400">
            {zeroCostNames.length > 0
              ? `${zeroCostNames.slice(0, 3).join(", ")}${zeroCostNames.length > 3 ? ` and ${zeroCostNames.length - 3} more` : ""} ${zeroCostNames.length === 1 ? "has" : "have"} no cost — the supplier would read free goods.`
              : "One or more items have no cost — the supplier would read free goods."}{" "}
            Fix the costs on the Quote, or create the purchase order anyway.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={mutation.isPending}
              onClick={() => {
                setZeroCostNames(null);
                mutation.mutate({ lineIds: lastLineIds, skipArtwork: artworkOverride, skipZeroCost: true });
              }}
            >
              {mutation.isPending ? "Creating…" : "Create PO anyway"}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setZeroCostNames(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {(createdPos.length > 0 || orphanedPos.length > 0) && (
        <div className="mb-4 rounded-lg border border-brown-100 bg-cream-50 px-4 py-3 text-sm text-brown-500">
          {createdPos.length > 0 && (
            <>
              <p className="font-semibold">
                {createdPos.length} purchase order{createdPos.length === 1 ? "" : "s"} created
              </p>
              <ul className="mt-2 space-y-1">
                {createdPos.map((p) => (
                  <li key={p.id}>
                    <Link to={`/projects/${order.projectId}/pos/${p.id}`} className="font-semibold text-ink hover:text-muted">
                      PO #{p.poNumber} — {p.poType === "decoration_service" ? "decoration" : "supplier"} →
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
          {orphanedPos.length > 0 && (
            <div className={createdPos.length > 0 ? "mt-3" : ""}>
              <p className="font-semibold text-rust-600">
                {orphanedPos.length} purchase order{orphanedPos.length === 1 ? "" : "s"} flagged for review
              </p>
              <p className="mt-0.5 text-brown-400">Superseded by a re-staged decorator — review and cancel if no longer needed.</p>
              <ul className="mt-1 space-y-1">
                {orphanedPos.map((p) => (
                  <li key={p.id}>
                    <Link to={`/projects/${order.projectId}/pos/${p.id}`} className="font-semibold text-ink hover:text-muted">
                      PO #{p.poNumber} — flagged →
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
